<template>
    <header
        ref="headerWrapper"
        class="relative z-50 -mx-4"
        :style="headerWrapperStyle"
    >
        <div ref="header" :class="{ 'fixed top-0': isHeaderFixed }" class="bg-white border-b lg:border-none z-50 w-full py-2 lg:py-0">
            <div class="container m-auto">
                <section class="items-center justify-center gap-8 hidden lg:flex">
                    <LayoutHeaderTopLinks :data="data" link-class="leading-6 py-2 px-4 text-link" />
                </section>

                <section class="flex justify-between items-center    gap-6 lg:py-4">
                    <LayoutLogo :media="media" />

                    <div class="flex justify-end w-full items-center gap-2 lg:gap-4">
                        <div class="w-full max-w-[343px] lg:max-w-[550px] hidden md:block">
                            <LayoutHeaderSearch />
                        </div>

                        <LayoutHeaderActions />
                    </div>
                </section>
            </div>

            <MenuDesktop :navigation="navigation" :data="data" />
            <FlyoutContextMenu
                v-if="flyoutContext === 'menu'"
                :navigation="navigation"
                class="lg:hidden"
                :data="data"
            />
        </div>

        <FlyoutContextSearch v-if="flyoutContext === 'search'" class="md:hidden" />

        
    </header>
</template>

<script setup lang="ts">
import type { HblNavigation } from "@/utils/types";
import { useFlyout, useNuxtApp, useSharedHeightMobileMenu } from '#imports'
import { ref } from 'vue'
import { storeToRefs } from "pinia";

const loading = ref(null)
const nuxtApp = useNuxtApp()

const { flyoutContext, closeFlyout, flyoutState } = useFlyout()

const flyoutActive = ref(false)
const headerWrapper = ref<HTMLElement | null>(null)
const header = ref<HTMLElement | null>(null)
const headerWrapperStyle = ref<Partial<CSSStyleDeclaration>>()
const { isHeaderFixed } = storeToRefs(useSharedHeightMobileMenu())

const props = defineProps<{
    navigation: HblNavigation,
    data: any,
    media: any
}>()

function reserveHeaderSpace() {
    // By default the header is in normal flow and reserves its own space — no
    // padding, no measurement, no layout shift. Only while it is pinned
    // (isHeaderFixed → position: fixed, out of flow) do we reserve its height on
    // the wrapper so the content below doesn't jump. The ResizeObserver keeps
    // that reservation in sync while pinned.
    if (isHeaderFixed.value && headerWrapper.value && header.value) {
        headerWrapperStyle.value = { paddingBottom: `${header.value.offsetHeight}px` }
    } else {
        headerWrapperStyle.value = undefined
    }
}

const { width } = useWindowSize()

// Pin the header on mobile once its in-flow slot scrolls out of the viewport
// top. On desktop it stays in normal flow and never pins.
useIntersectionObserver(headerWrapper, ([entry]) => {
    isHeaderFixed.value = width.value <= 1024 ? !entry.isIntersecting : false
}, { threshold: 0 })

// Keep the reserved height in sync while pinned (also runs on initial observe).
useResizeObserver(header, reserveHeaderSpace)

// Clear a mobile-pinned state when resizing up to desktop — the
// IntersectionObserver won't refire without a scroll, which would otherwise
// leave the header wrongly pinned.
watch(width, (value) => {
    if (value > 1024) {
        isHeaderFixed.value = false
    }
})

nuxtApp.hook('page:finish', () => {
    // @ts-ignore
    loading.value?.finish()
    window.scrollTo(0, 0)
    closeFlyout()
})

watch(flyoutState, (value) => {
    if (value) {
        // Unselect option
        flyoutActive.value = value
    } else {
        flyoutActive.value = false
    }
})

// Add/remove the reserved space exactly when the header pins/unpins on scroll.
watch(isHeaderFixed, reserveHeaderSpace)

useHead({
    bodyAttrs: {
        class: computed(() => (flyoutActive.value ? 'overflow-hidden' : ''))
    }
});
</script>
